import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Mail, X } from 'lucide-react';
import axios from 'axios';
import { BASE_URL } from '../../../config/axios';
import { toast } from 'react-toastify';

const LetterModal = ({ open, onClose, contractor, project }) => {
    const [letterHeadings, setLetterHeadings] = useState([]);
    const [letterData, setLetterData] = useState([]);
    const [drawerInfo, setDrawerInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadingStage, setLoadingStage] = useState('');

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const getHeadings = async () => {
        try {
            setLoadingStage('Fetching letter headings...');
            setLoadingProgress(30);
            const res = await axios.get(`${BASE_URL}/siteproject/LetterHeadingfilter/59408e4e-a455-46b6-b3db-8deaa0ae4475/`);
            setLetterHeadings(res.data);
            setLoadingProgress(50);
            return true;
        } catch (error) {
            toast.error('Failed to fetch letter headings');
            console.error('Error fetching headings:', error);
            return false;
        }
    };

    const getLetterData = async () => {
        try {
            setLoadingStage('Fetching letter records...');
            setLoadingProgress(60);
            const res = await axios.get(
                `${BASE_URL}/siteproject/letterrecordsoptimized/filter/null/null/null/null/${project?.id}/contractor/null/null/null/${contractor?.id}/null/`
            );
            setLetterData(res.data);
            setLoadingProgress(100);
            return true;
        } catch (error) {
            toast.error('Failed to fetch letter data');
            console.error('Error fetching letter data:', error);
            return false;
        }
    };

    const fetchAllData = async () => {
        setLoading(true);
        setLoadingProgress(0);
        setLoadingStage('Initializing...');

        try {
            await new Promise(resolve => setTimeout(resolve, 100));
            setLoadingProgress(10);

            setLoadingStage('Fetching letter headings...');
            const headingsSuccess = await getHeadings();

            if (!headingsSuccess) {
                setLoading(false);
                return;
            }

            setLoadingProgress(50);

            setLoadingStage('Fetching letter records...');
            const dataSuccess = await getLetterData();

            if (!dataSuccess) {
                setLoading(false);
                return;
            }

            setLoadingProgress(100);

            setTimeout(() => {
                setLoading(false);
            }, 300);

        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load letter data');
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchAllData();
        }
    }, [open]);

    useEffect(() => {
        if (open) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = '';
        return () => { document.body.style.overflow = ''; };
    }, [open]);

    if (!open || !contractor) return null;

    const isOutTAT = (letter) => {
        if (!letter.end_date) return false;
        const endDate = new Date(letter.end_date);
        endDate.setHours(0, 0, 0, 0);
        return endDate < today;
    };

    const buildTypeRows = () => {
        const ALLOWED_WPB = ['contractor', 'For Both'];
        const filteredHeadings = letterHeadings.filter(h =>
            ALLOWED_WPB.includes(h.work_performed_by)
        );

        const buckets = {};

        for (const letter of letterData) {
            const lt = letter.lettertype?.toLowerCase().trim() || 'other';

            const matchedHeading = filteredHeadings.find(h =>
                h.name.toLowerCase().trim() === lt
            );

            const bucketKey = matchedHeading ? matchedHeading.name : 'Other';

            if (!buckets[bucketKey]) buckets[bucketKey] = [];
            buckets[bucketKey].push(letter);
        }

        return Object.entries(buckets).map(([name, total]) => {
            const pending = total.filter(l =>
                l.letterExchange === 'receive' && l.status === 'pending'
            );
            const inTAT = pending.filter(l => !isOutTAT(l));
            const outTAT = pending.filter(l => isOutTAT(l));
            const closed = total.filter(l => l.status === 'close' || (l.status === 'pending' && l.letterExchange === 'send'));

            return { name, total, pending, inTAT, outTAT, closed };
        });
    };

    const typeRows = buildTypeRows();

    const grandTotal = typeRows.reduce((s, r) => s + r.total.length, 0);
    const grandPending = typeRows.reduce((s, r) => s + r.pending.length, 0);
    const grandClosed = grandTotal - grandPending;

    const NumBtn = ({ letters, label, className }) => {
        if (letters.length === 0) {
            return <span className={`letter-count-zero ${className}`}>0</span>;
        }
        return (
            <button
                className={`letter-count-btn ${className}`}
                onClick={() => setDrawerInfo({ title: label, letters })}
            >
                {letters.length}
            </button>
        );
    };

    const LetterDrawer = () => {
        if (!drawerInfo) return null;
        return (
            <div className="letter-drawer">
                <div className="letter-drawer-header">
                    <span>{drawerInfo.title}</span>
                    <button onClick={() => setDrawerInfo(null)}><X size={14} /></button>
                </div>
                <div className="letter-drawer-list">
                    {drawerInfo.letters.length === 0 ? (
                        <div className="letter-drawer-empty">No letters</div>
                    ) : drawerInfo.letters.map(l => (
                        <div className="letter-drawer-item" key={l.id}>
                            <div className="ldi-left">
                                <div className="ldi-num">{l.letter_num}</div>
                                <div className="ldi-subj">{l.subject}</div>
                                <div className="ldi-meta">
                                    <span className={`ldi-badge ${l.status === 'close' ? 'badge-close' : isOutTAT(l) ? 'badge-outtat' : 'badge-intat'}`}>
                                        {l.status === 'close' ? 'Closed' : isOutTAT(l) ? 'Out TAT' : 'In TAT'}
                                    </span>
                                    <span>{l.letterExchange} · {l.letter_date} · {l.lettertype}</span>
                                </div>

                                {l.documents?.length > 0 && (
                                    <div className="ldi-docs">
                                        {l.documents.map(doc => (
                                            <a
                                                key={doc.id}
                                                href={doc.document_file}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="ldi-doc-btn"
                                                title={doc.document_name}
                                            >
                                                📄 {doc.document_name}
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="ldi-date">{l.end_date ? `due: ${l.end_date}` : ''}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const LoadingSpinner = () => (
        <div className="letter-loading-overlay">
            <div className="letter-loading-container">
                <div className="letter-loading-spinner">
                    <div className="spinner-ring"></div>
                </div>
                <div className="letter-loading-progress">
                    <div className="progress-bar-container">
                        <div
                            className="progress-bar-fill"
                            style={{ width: `${loadingProgress}%` }}
                        ></div>
                    </div>
                    <div className="progress-percentagee">{loadingProgress}%</div>
                </div>
                <div className="letter-loading-stage">{loadingStage}</div>
                <div className="letter-loading-message">
                    Loading letter records for {contractor?.name}...
                </div>
            </div>
        </div>
    );

    const modalContent = (
        <div className="letter-modal-overlay" onClick={onClose}>
            <div className="letters-modal" onClick={e => e.stopPropagation()}>
                {loading && <LoadingSpinner />}

                <div className="letter-modal-header">
                    <div className="letter-modal-title">
                        <div className="letter-modal-icon-wrap">
                            <Mail size={18} color="white" />
                        </div>
                        <div>
                            <h3>Letter Summary</h3>
                            <p>{contractor?.name}</p>
                        </div>
                    </div>
                    <button className="letter-close-btn" onClick={onClose}><X size={18} /></button>
                </div>

                <div className="letter-modal-stats">
                    <div className="lms-card total-card">
                        <div className="lms-icon">📬</div>
                        <div className="lms-value">{grandTotal}</div>
                        <div className="lms-label">Total</div>
                    </div>
                    <div className="lms-card pending-card">
                        <div className="lms-icon">⏳</div>
                        <div className="lms-value">{grandPending}</div>
                        <div className="lms-label">Pending</div>
                    </div>
                    <div className="lms-card closed-card">
                        <div className="lms-icon">✅</div>
                        <div className="lms-value">{grandClosed}</div>
                        <div className="lms-label">Closed</div>
                    </div>
                </div>

                {typeRows.length > 0 && (
                    <div className="letter-type-breakdown">
                        <div className="ltb-title">By Letter Type</div>
                        <div className='table-box scroll-container-table' style={{ height: "30vh", minHeight: "10vh" }}>
                            <table className="table-css ltb-table">
                                <thead className="table-heading">
                                    <tr className="custom-table-head-tr">
                                        <th>Type</th>
                                        <th>Total</th>
                                        <th>Pending</th>
                                        <th>In TAT</th>
                                        <th>Out TAT</th>
                                        <th>Closed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {typeRows.map(row => (
                                        <tr key={row.name}>
                                            <td className="ltb-type-name">{row.name}</td>
                                            <td><NumBtn letters={row.total} label={`${row.name} — All`} className="btn-total" /></td>
                                            <td><NumBtn letters={row.pending} label={`${row.name} — Pending`} className="btn-pending" /></td>
                                            <td><NumBtn letters={row.inTAT} label={`${row.name} — In TAT`} className="btn-intat" /></td>
                                            <td><NumBtn letters={row.outTAT} label={`${row.name} — Out TAT`} className="btn-outtat" /></td>
                                            <td><NumBtn letters={row.closed} label={`${row.name} — Closed`} className="btn-closed" /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <LetterDrawer />

                {project && (
                    <div className="letter-modal-info">
                        <div className="lmi-row">
                            <span className="lmi-label">Project</span>
                            <span className="lmi-value">{project?.project_short_name}</span>
                        </div>
                        <div className="lmi-row">
                            <span className="lmi-label">Code</span>
                            <span className="lmi-value">{project?.site_prcode}</span>
                        </div>
                    </div>
                )}

                <div className="letter-modal-footer">
                    <button className="letter-footer-close-btn" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default LetterModal;